'use client';

import React from 'react';
import type { Quote } from '@/types';
import { Plus, Clock, Search, Filter } from 'lucide-react';
import { AircraftIcon, StatusIcon } from '../ui/IconSet';

type QuoteListProps = {
  quotes: Quote[];
  premiumMode?: boolean;
  onNewQuote: () => void;
  onSelectQuote?: (quote: Quote) => void;
  onViewQuote?: (quote: Quote) => void;
  onRequestSend?: (quote: Quote) => void;
  onViewPassengers?: (quote: Quote) => void;
};

const QuoteList: React.FC<QuoteListProps> = ({ quotes, premiumMode = false, onNewQuote, onSelectQuote, onViewQuote, onRequestSend, onViewPassengers }) => {
  const dark = premiumMode;
  const [query, setQuery] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<'ALL' | Quote['status']>('ALL');
  const [sentFilter, setSentFilter] = React.useState<'ALL' | 'SENT' | 'UNSENT'>('ALL');
  const [viewMode, setViewMode] = React.useState<'LIST' | 'CALENDAR'>('LIST');
  const [calendarMonth, setCalendarMonth] = React.useState(() => {
    const now = new Date();
    return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedCalendarDate, setSelectedCalendarDate] = React.useState<string>('');
  const charterQuotes = Array.isArray(quotes) ? quotes.filter(q => q.productMode === 'CHARTER') : [];
  const filteredQuotes = charterQuotes.filter(q => {
    if (!query.trim()) return true;
    const haystack = [
      q.customerName,
      q.aircraftModel,
      q.tailNumber,
      q.bookingId,
      q.pnrCode,
      q.date,
      q.status,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
  }).filter(q => {
    if (statusFilter === 'ALL') return true;
    return q.status === statusFilter;
  }).filter(q => {
    if (sentFilter === 'ALL') return true;
    const hasZoho = !!q.zohoEstimateId || !!q.zohoEstimateNumber;
    const isSent = !!q.sentAt || q.status === 'SENT' || q.status === 'APPROVED' || q.status === 'REJECTED' || hasZoho;
    return sentFilter === 'SENT' ? isSent : !isSent;
  });

  const getLeadVerification = (quote: Quote): { label: 'Needs Verification' | 'Known Contact' | 'Verified (Zoho)'; className: string } | null => {
    const intakeMeta = (quote as any)?.costs?.intake || {};
    if (String(intakeMeta?.source || '').toUpperCase() !== 'EMAIL_INTAKE') return null;
    const status = String(intakeMeta?.verificationStatus || 'UNVERIFIED').toUpperCase();
    const hasZohoContact = !!String((quote as any)?.Customer?.zohoContactId || '').trim();
    const effectiveStatus = status === 'UNVERIFIED' && hasZohoContact ? 'TRUSTED' : status;
    if (effectiveStatus === 'VERIFIED') {
      return { label: 'Verified (Zoho)', className: 'border-emerald-200 bg-emerald-50 text-emerald-700' };
    }
    if (effectiveStatus === 'TRUSTED') {
      return { label: 'Known Contact', className: 'border-sky-200 bg-sky-50 text-sky-700' };
    }
    return { label: 'Needs Verification', className: 'border-amber-200 bg-amber-50 text-amber-700' };
  };

  const getQuoteDate = (quote: Quote): Date | null => {
    const candidates = [quote.date, quote.sentAt, quote.clientApprovalAt].filter(Boolean) as string[];
    for (const raw of candidates) {
      const parsed = new Date(raw);
      if (!Number.isNaN(parsed.getTime())) return parsed;
      const short = String(raw).slice(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(short)) {
        const fromShort = new Date(`${short}T00:00:00.000Z`);
        if (!Number.isNaN(fromShort.getTime())) return fromShort;
      }
    }
    return null;
  };

  const toDayKey = (date: Date) => {
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getQuoteTimelineTime = (quote: Quote): number => {
    const runtimeQuote = quote as Quote & { updatedAt?: string; createdAt?: string };
    const candidates = [
      quote.clientApprovalAt,
      quote.sentAt,
      runtimeQuote.updatedAt,
      runtimeQuote.createdAt,
      quote.date,
    ].filter(Boolean) as string[];
    for (const raw of candidates) {
      const parsed = new Date(raw).getTime();
      if (!Number.isNaN(parsed)) return parsed;
      const short = String(raw).slice(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(short)) {
        const fromShort = new Date(`${short}T00:00:00.000Z`).getTime();
        if (!Number.isNaN(fromShort)) return fromShort;
      }
    }
    return 0;
  };

  const calendarDayMap = React.useMemo(() => {
    const map = new Map<string, Quote[]>();
    filteredQuotes.forEach((quote) => {
      const date = getQuoteDate(quote);
      if (!date) return;
      const key = toDayKey(date);
      const existing = map.get(key) || [];
      existing.push(quote);
      map.set(key, existing);
    });
    map.forEach((arr, key) => {
      arr.sort((a, b) => getQuoteTimelineTime(b) - getQuoteTimelineTime(a));
      map.set(key, arr);
    });
    return map;
  }, [filteredQuotes]);

  const monthDate = React.useMemo(() => {
    const parsed = new Date(`${calendarMonth}-01T00:00:00.000Z`);
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  }, [calendarMonth]);

  const calendarCells = React.useMemo(() => {
    const year = monthDate.getUTCFullYear();
    const month = monthDate.getUTCMonth();
    const firstDay = new Date(Date.UTC(year, month, 1));
    const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const startWeekday = firstDay.getUTCDay();
    const cells: Array<{ key: string; label: number; inMonth: boolean }> = [];
    for (let i = 0; i < startWeekday; i += 1) {
      cells.push({ key: `pad-start-${i}`, label: 0, inMonth: false });
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      cells.push({ key, label: day, inMonth: true });
    }
    while (cells.length % 7 !== 0) {
      cells.push({ key: `pad-end-${cells.length}`, label: 0, inMonth: false });
    }
    return cells;
  }, [monthDate]);

  React.useEffect(() => {
    if (viewMode !== 'CALENDAR') return;
    const keys = Array.from(calendarDayMap.keys() as IterableIterator<string>)
      .filter((k) => k.startsWith(`${calendarMonth}-`))
      .sort();
    if (selectedCalendarDate && keys.includes(selectedCalendarDate)) return;
    setSelectedCalendarDate(keys[0] || `${calendarMonth}-01`);
  }, [viewMode, calendarMonth, calendarDayMap, selectedCalendarDate]);

  const selectedDayQuotes = React.useMemo(
    () => [...(calendarDayMap.get(selectedCalendarDate) || [])].sort((a, b) => getQuoteTimelineTime(b) - getQuoteTimelineTime(a)),
    [calendarDayMap, selectedCalendarDate]
  );

  const monthLabel = monthDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });

  const renderQuoteCard = (q: Quote) => {
    const intakeMeta = (q as any)?.costs?.intake;
    const fromEmailIntake = intakeMeta?.source === 'EMAIL_INTAKE';
    const leadVerification = getLeadVerification(q);
    const hasZoho = !!q.zohoEstimateId || !!q.zohoEstimateNumber;
    const isSent = !!q.sentAt || q.status === 'SENT' || q.status === 'APPROVED' || q.status === 'REJECTED' || hasZoho;
    const canShowZoho = hasZoho;
    const approvalState = q.status === 'APPROVED' ? 'Approved' : q.status === 'REJECTED' ? 'Rejected' : '';
    const primaryState = approvalState || (isSent ? 'Sent' : q.status === 'PENDING_APPROVAL' ? 'Pending' : 'Unsent');
    const passengerCount = q.passengerManifest?.passengerCount || 0;
    const hasManifest = !!q.passengerManifest;
    const manifestIssued = !!q.passengerManifest?.isIssued;
    const passengerLabel =
      manifestIssued
        ? `P.L Issued (${passengerCount})`
        : passengerCount > 0
        ? `P.L ${passengerCount}`
        : hasManifest
        ? 'P.L Pending'
        : 'P.L Missing';
    return (
      <button
        type="button"
        key={q.id}
        onClick={() => onViewQuote?.(q)}
        className={`w-full rounded-2xl border p-5 transition-all group text-left ${
          dark
            ? 'dashboard-card'
            : 'bg-white border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300'
        }`}
      >
        <div className="flex items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className={`flex items-center justify-center w-16 h-16 rounded-xl shadow-sm ${
              dark ? 'bg-gradient-to-br from-amber-800/30 to-slate-800/50' : 'bg-gradient-to-br from-indigo-50 to-slate-100'
            }`}>
              <AircraftIcon className={`w-10 h-10 ${dark ? 'text-amber-200' : 'text-slate-800'}`} />
            </div>
            <div className={`flex flex-wrap items-center gap-4 text-xs uppercase tracking-wider ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
              {q.quoteNumber && (
                <span className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[10px] font-bold tracking-wider text-sky-700">
                  {q.quoteNumber}
                </span>
              )}
              <span>{Array.isArray(q.legs) ? q.legs.length : 0} Sectors</span>
              <span>{q.date}</span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {(q.totalBlockTimeMinutes / 60).toFixed(1)} hrs
              </span>
              {q.bookingId && <span>Booking: {q.bookingId}</span>}
              {q.pnrCode && <span>PNR: {q.pnrCode}</span>}
              {q.customerName && <span>Client: {q.customerName}</span>}
              {q.issuedBy && <span>Issued by: {q.issuedBy}</span>}
              {fromEmailIntake && <span className="text-sky-600 font-semibold">From Email Intake</span>}
            </div>
          </div>

          <div className="flex items-center justify-end gap-4 flex-shrink-0">
            <div className={`text-right pr-4 border-r ${dark ? 'border-amber-400/20' : 'border-slate-200'}`}>
              <span className={`block text-[10px] uppercase tracking-wider font-semibold ${dark ? 'text-slate-400' : 'text-slate-500'}`}>Revenue</span>
              <p className={`text-xl font-bold tracking-tight ${dark ? 'text-white' : 'text-slate-900'}`}>
                ${Math.round(q.totalUSD).toLocaleString()}
              </p>
            </div>

            <div className="flex items-center gap-2.5">
              <span
                className={`status-chip inline-flex items-center gap-1 px-2 py-1 ${
                  primaryState === 'Approved'
                    ? 'chip-approved'
                    : primaryState === 'Rejected'
                    ? 'chip-rejected'
                    : isSent
                    ? 'chip-sent'
                    : 'chip-default'
                }`}
              >
                <StatusIcon state={primaryState} className="w-3 h-3" />
                <span className="uppercase text-[11px]">{primaryState}</span>
              </span>

              {onViewPassengers && (
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    onViewPassengers(q);
                  }}
                  className={`badge badge-pill badge-compact text-[10px] px-2 py-1 ${
                    manifestIssued
                      ? 'badge-success'
                      : passengerCount > 0
                      ? 'badge-success'
                      : hasManifest
                      ? 'badge-warning'
                      : 'badge-ghost text-slate-500'
                  }`}
                >
                  {passengerLabel}
                </button>
              )}

              {canShowZoho && (
                <span className="badge badge-zoho badge-pill text-[10px] px-2 py-1">
                  Zoho: {q.zohoEstimateNumber || 'Linked'}
                </span>
              )}
              {q.quoteNumber && (
                <span className="badge badge-pill text-[10px] px-2 py-1 border border-sky-200 bg-sky-50 text-sky-700">
                  {q.quoteNumber}
                </span>
              )}
              {fromEmailIntake && (
                <span className="badge badge-pill text-[10px] px-2 py-1 border border-sky-200 bg-sky-50 text-sky-700">
                  Intake
                </span>
              )}
              {leadVerification && (
                <span className={`badge badge-pill text-[10px] px-2 py-1 border ${leadVerification.className}`}>
                  {leadVerification.label}
                </span>
              )}
            </div>

            <div className={`flex items-center gap-2.5 ml-2 pl-3 border-l ${dark ? 'border-amber-400/20' : 'border-slate-200'}`}>
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  onSelectQuote?.(q);
                }}
                className="btn-action btn-ghost btn-sm text-[10px] px-3 py-1.5 rounded-lg font-semibold"
              >
                Edit
              </button>
              {!isSent && (
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    onRequestSend?.(q);
                  }}
                  className="btn-action btn-sm text-[10px] px-3 py-1.5 rounded-lg font-semibold bg-indigo-600 text-white shadow-md hover:shadow-lg border border-indigo-700"
                >
                  Send
                </button>
              )}
            </div>
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className={`text-[11px] font-bold uppercase tracking-[0.25em] mb-2 ${dark ? 'text-amber-400' : 'text-indigo-500'}`}>Charter Console</p>
          <h2 className={`text-4xl font-bold tracking-tight ${dark ? 'text-white' : 'text-slate-900'}`}>Mission Ledger</h2>
          <p className={`text-sm mt-2 ${dark ? 'text-amber-300' : 'text-slate-600'}`}>
            Draft, authorize, and manage bespoke charter missions.
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className={`flex items-center gap-2 rounded-full border px-4 py-2.5 shadow-sm ${
            dark ? 'border-amber-400/30 bg-slate-800/60' : 'border-slate-200 bg-white'
          }`}>
            <Search className={`h-4 w-4 ${dark ? 'text-amber-300' : 'text-slate-500'}`} />
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search customer, booking, PNR, airframe, date"
              className={`w-full sm:w-72 bg-transparent text-sm font-medium outline-none ${dark ? 'text-amber-200' : 'text-slate-800 placeholder:text-slate-400'}`}
            />
          </div>
          <button
            type="button"
            onClick={onNewQuote}
            className={dark ? 'btn-premium btn-glow rounded-full px-6 py-3 font-semibold text-sm uppercase tracking-wider' : 'rounded-full px-6 py-3 font-semibold text-sm uppercase tracking-wider bg-indigo-600 text-white hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-500/30'}
          >
            New Mission
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className={`flex items-center gap-1 rounded-full border px-2 py-1 ${dark ? 'border-amber-400/30 bg-slate-800/60' : 'border-slate-200 bg-slate-50'}`}>
          {(['ALL', 'DRAFT', 'PENDING_APPROVAL', 'SENT', 'APPROVED', 'REJECTED'] as const).map(status => (
            <button
              key={status}
              type="button"
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                statusFilter === status
                  ? dark ? 'bg-amber-400 text-slate-900' : 'bg-indigo-600 text-white'
                  : dark ? 'text-amber-200 hover:text-white' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              {status === 'PENDING_APPROVAL' ? 'Pending' : status.toLowerCase()}
            </button>
          ))}
        </div>
        <div className={`flex items-center gap-1 rounded-full border px-2 py-1 ${dark ? 'border-amber-400/30 bg-slate-800/60' : 'border-slate-200 bg-slate-50'}`}>
          {(['ALL', 'SENT', 'UNSENT'] as const).map(label => (
            <button
              key={label}
              type="button"
              onClick={() => setSentFilter(label)}
              className={`px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                sentFilter === label
                  ? dark ? 'bg-amber-400 text-slate-900' : 'bg-indigo-600 text-white'
                  : dark ? 'text-amber-200 hover:text-white' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              {label.toLowerCase()}
            </button>
          ))}
        </div>
        <div className={`ml-auto flex items-center gap-1 rounded-full border px-2 py-1 ${dark ? 'border-amber-400/30 bg-slate-800/60' : 'border-slate-200 bg-slate-50'}`}>
          {(['LIST', 'CALENDAR'] as const).map(mode => (
            <button
              key={mode}
              type="button"
              onClick={() => setViewMode(mode)}
              className={`px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                viewMode === mode
                  ? dark ? 'bg-amber-400 text-slate-900' : 'bg-indigo-600 text-white'
                  : dark ? 'text-amber-200 hover:text-white' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              {mode}
            </button>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div className="h-px bg-slate-200" />

      {/* Quote Cards */}
      {viewMode === 'LIST' && (
      <div className="space-y-4">
        {filteredQuotes.map(renderQuoteCard)}
      </div>
      )}

      {viewMode === 'CALENDAR' && (
        <div className="space-y-5">
          <div className={`rounded-2xl border p-4 ${dark ? 'border-amber-400/30 bg-slate-900/50' : 'border-slate-200 bg-white'}`}>
            <div className="mb-3 flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  const prev = new Date(Date.UTC(monthDate.getUTCFullYear(), monthDate.getUTCMonth() - 1, 1));
                  setCalendarMonth(`${prev.getUTCFullYear()}-${String(prev.getUTCMonth() + 1).padStart(2, '0')}`);
                }}
                className={`rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${dark ? 'border-amber-300/40 text-amber-200' : 'border-slate-300 text-slate-700'}`}
              >
                Prev
              </button>
              <div className={`text-sm font-black uppercase tracking-[0.18em] ${dark ? 'text-amber-200' : 'text-slate-800'}`}>{monthLabel}</div>
              <button
                type="button"
                onClick={() => {
                  const next = new Date(Date.UTC(monthDate.getUTCFullYear(), monthDate.getUTCMonth() + 1, 1));
                  setCalendarMonth(`${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, '0')}`);
                }}
                className={`rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${dark ? 'border-amber-300/40 text-amber-200' : 'border-slate-300 text-slate-700'}`}
              >
                Next
              </button>
            </div>
            <div className="mb-2 grid grid-cols-7 gap-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className={`px-2 text-[10px] font-black uppercase tracking-[0.12em] ${dark ? 'text-amber-300/80' : 'text-slate-500'}`}>
                  {day}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-2">
              {calendarCells.map(cell => {
                if (!cell.inMonth) return <div key={cell.key} className="h-20 rounded-xl border border-transparent" />;
                const dayQuotes = calendarDayMap.get(cell.key) || [];
                const selected = selectedCalendarDate === cell.key;
                return (
                  <button
                    type="button"
                    key={cell.key}
                    onClick={() => setSelectedCalendarDate(cell.key)}
                    className={`h-20 rounded-xl border p-2 text-left transition ${
                      selected
                        ? dark ? 'border-amber-300 bg-amber-300/20' : 'border-indigo-300 bg-indigo-50'
                        : dark ? 'border-amber-400/20 bg-slate-800/40 hover:border-amber-300/40' : 'border-slate-200 bg-slate-50 hover:border-slate-300'
                    }`}
                  >
                    <div className={`text-xs font-black ${dark ? 'text-amber-100' : 'text-slate-800'}`}>{cell.label}</div>
                    <div className={`mt-1 text-[10px] font-semibold ${dark ? 'text-amber-200/90' : 'text-slate-600'}`}>
                      {dayQuotes.length} mission{dayQuotes.length === 1 ? '' : 's'}
                    </div>
                    {dayQuotes[0]?.quoteNumber && (
                      <div className={`mt-1 truncate text-[10px] font-black ${dark ? 'text-amber-100' : 'text-indigo-700'}`}>
                        {dayQuotes[0].quoteNumber}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={`rounded-2xl border p-4 ${dark ? 'border-amber-400/30 bg-slate-900/50' : 'border-slate-200 bg-white'}`}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className={`text-sm font-black uppercase tracking-[0.16em] ${dark ? 'text-amber-200' : 'text-slate-800'}`}>
                Day History • {selectedCalendarDate || '—'}
              </h3>
              <span className={`text-xs font-semibold ${dark ? 'text-amber-200/90' : 'text-slate-600'}`}>
                {selectedDayQuotes.length} record(s)
              </span>
            </div>
            {selectedDayQuotes.length === 0 ? (
              <div className={`rounded-xl border border-dashed p-4 text-sm ${dark ? 'border-amber-300/30 text-amber-200/80' : 'border-slate-300 text-slate-500'}`}>
                No charter mission history for this date.
              </div>
            ) : (
              <div className="space-y-4">
                {selectedDayQuotes.map(renderQuoteCard)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Empty State */}
      {filteredQuotes.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 border border-dashed border-slate-300 rounded-2xl bg-slate-50">
          <p className="text-lg font-semibold text-slate-900">No missions yet</p>
          <p className="text-sm text-slate-500 mt-1 mb-6">Create your first charter quote to get started</p>
          <button
            type="button"
            onClick={onNewQuote}
            className="flex items-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-full font-semibold text-sm uppercase tracking-wider hover:bg-indigo-700 transition-all"
          >
            <Plus className="w-4 h-4" />
            New Mission
          </button>
        </div>
      )}
    </div>
  );
};

export default QuoteList;
